import axios from 'axios'
import tokenService from './token'
import { createApiUrl, LONG_REQUEST_TIMEOUT_MS } from '../config/api'

// Glossary automation lives under the terminology app, not the resource-side
// /api/automation/ app — terminology is not a polymorphic Resource, so it has its
// own scan/flag pipeline behind the terminology admin permission.
const baseUrl = createApiUrl('/api/terminology/admin/automation/')

const flagsUrl = `${baseUrl}flags/`
const scanUrl = `${baseUrl}scan/`
const settingsUrl = `${baseUrl}settings/`
const flagActionUrl = (id, action) => `${baseUrl}flags/${id}/${action}/`

// The flag list's filter params — shared with the filter UI and the reducer so the
// three can't drift.
export const FLAG_FILTER_KEYS = ['status', 'kind']

// Query string for a page of flags: page plus any non-empty whitelisted filter.
const flagsQuery = (params = {}) => {
    const search = new URLSearchParams({ page: params.page || 1 })
    for (const key of FLAG_FILTER_KEYS) {
        if (params[key]) search.append(key, params[key])
    }
    return search.toString()
}

// A page of automation flags: the StandardResultsSetPagination envelope
// { count, next, previous, total_pages, current_page, results } where each result is
// { id, kind, status, reason, created, term, source }.
const getFlags = async (params = {}) => {
    const res = await axios.get(`${flagsUrl}?${flagsQuery(params)}`, tokenService.getAuthHeader())
    return res.data
}

// Run a scan pass synchronously. Returns
// { ran, created, resolved, suppressed, skipped_checks } — ran === false means
// automation is switched off in settings, so nothing was checked.
// Probes each source URL serially (LinkCheckService, 10s per URL), so this can
// legitimately run past the global default timeout (issue #407).
const runScan = async () => {
    const res = await axios.post(scanUrl, {}, { ...tokenService.getAuthHeader(), timeout: LONG_REQUEST_TIMEOUT_MS })
    return res.data
}

// Both flag actions return the updated flag, so callers never have to guess the new status.
const resolveFlag = async (id) => {
    const res = await axios.post(flagActionUrl(id, 'resolve'), {}, tokenService.getAuthHeader())
    return res.data
}

const dismissFlag = async (id) => {
    const res = await axios.post(flagActionUrl(id, 'dismiss'), {}, tokenService.getAuthHeader())
    return res.data
}

// { enabled, max_age_years, link_check_enabled }
const getSettings = async () => {
    const res = await axios.get(settingsUrl, tokenService.getAuthHeader())
    return res.data
}

// Save the settings form (same shape as getSettings). Returns the persisted payload;
// throws with a 400 body on validation failure.
const updateSettings = async (payload) => {
    const res = await axios.put(settingsUrl, payload, tokenService.getAuthHeader())
    return res.data
}

const glossaryAutomationService = {
    getFlags,
    runScan,
    resolveFlag,
    dismissFlag,
    getSettings,
    updateSettings,
}

export default glossaryAutomationService
